<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import { getFeatureFlags, getFeatureFlag, addUserToFlag, removeUserFromFlag, searchBackOfficeUsers, FeatureFlag } from './FeatureFlags.access';

    const state = reactive<{
        flags: FeatureFlag[];
        selectedFlag: FeatureFlag | null;
        loading: boolean;
        userToAdd: string;
        userOptions: string[];
    }>({
        flags: [],
        selectedFlag: null,
        loading: false,
        userToAdd: '',
        userOptions: [],
    });

    let userOptionsDebounceTimer: ReturnType<typeof setTimeout> | undefined;

    function onUserToAddInput(): void {
        clearTimeout(userOptionsDebounceTimer);
        userOptionsDebounceTimer = setTimeout(async () => {
            const users = await searchBackOfficeUsers(state.userToAdd);
            state.userOptions = users.map((u) => u.Email);
        }, 300);
    }

    onMounted(async () => {
        state.loading = true;
        try {
            state.flags = await getFeatureFlags();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load feature flags' });
        } finally {
            state.loading = false;
        }
    });

    async function selectFlag(flag: FeatureFlag): Promise<void> {
        try {
            state.selectedFlag = await getFeatureFlag(flag.FeatureId);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load feature flag' });
        }
    }

    async function addUser(): Promise<void> {
        if (!state.selectedFlag || !state.userToAdd) return;
        try {
            const user = await addUserToFlag(state.selectedFlag.FeatureId, state.userToAdd);
            state.selectedFlag.Users.push(user);
            state.userToAdd = '';
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Added user' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Failed', content: 'Could not add user' });
        }
    }

    async function removeUser(userId: string): Promise<void> {
        if (!state.selectedFlag) return;
        try {
            await removeUserFromFlag(state.selectedFlag.FeatureId, userId);
            state.selectedFlag.Users = state.selectedFlag.Users.filter((u) => u.UserId !== userId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Removed user' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Failed', content: 'Could not remove user' });
        }
    }
</script>

<template>
    <h2>Manage Feature Flags</h2>

    <div class="row">
        <div class="col-sm-12 col-md-6">
            <h2 class="h4">Select feature flag</h2>
            <div v-if="state.loading">Loading...</div>
            <ul v-else class="tag-list list-unstyled clearfix">
                <li v-for="flag in state.flags" :key="flag.FeatureId">
                    <button type="button" class="tag text-md" :class="{ active: state.selectedFlag?.FeatureId === flag.FeatureId }" @click="selectFlag(flag)">
                        {{ flag.Name }}
                    </button>
                </li>
            </ul>
        </div>

        <div class="col-sm-12 col-md-6">
            <div v-if="state.selectedFlag">
                <h2 class="h4">Selected feature flag will be added to the email address entered below</h2>

                <form autocomplete="off" @submit.prevent="addUser">
                    <div class="search-group form-group">
                        <label for="feature-flag-email">Add user by email:</label>
                        <div class="input-group">
                            <span class="input-group-addon">@</span>
                            <input
                                id="feature-flag-email"
                                v-model="state.userToAdd"
                                @input="onUserToAddInput"
                                type="text"
                                class="form-control"
                                list="feature-flag-email-options"
                                placeholder="Enter email address here" />
                            <datalist id="feature-flag-email-options">
                                <option v-for="email in state.userOptions" :key="email" :value="email" />
                            </datalist>
                            <span class="input-group-btn">
                                <button type="submit" class="btn btn-primary">Add</button>
                            </span>
                        </div>
                    </div>
                </form>

                <h3>
                    <small>Users with the feature flag:</small>
                    <br />
                    {{ state.selectedFlag.Name }}
                </h3>

                <table class="table table-hover table-sticky-headers">
                    <thead>
                        <tr>
                            <th>Email address</th>
                            <th class="text-right">Remove flag from user</th>
                        </tr>
                    </thead>
                    <tbody class="td-align-middle">
                        <tr v-for="user in state.selectedFlag.Users" :key="user.UserId">
                            <td>{{ user.Email }}</td>
                            <td class="text-right">
                                <ConfirmButton
                                    buttonClasses="btn-default btn-xs"
                                    buttonText="Remove"
                                    @confirm="removeUser(user.UserId)"
                                    undoButton></ConfirmButton>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    export default {
        name: 'FeatureFlags',
    };
</script>
